import type {
  AuthenticatedUser,
  LoginInput,
  RegisterInput,
  UpdateProfileInput,
} from '@discordo/shared';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { apiFetch, setEmailNotVerifiedHandler, setUnauthorizedHandler } from '../lib/api';

/**
 * Session state for the whole SPA.
 *
 * `user` is the single source of truth about being logged in: the router shows
 * the auth screens while it is null and the app once it is filled. Nothing is
 * mirrored into local storage — the session lives in an httpOnly cookie the
 * client cannot read, so the server is asked once on boot and after that the
 * state only changes through the operations below.
 *
 * `user.emailVerified` is the second thing the router reads. An unconfirmed
 * account is signed in and gets the confirmation screen, not the app, which
 * mirrors what the API allows: the session is valid, the channels are not.
 *
 * That flag is kept honest from two directions: what `GET /api/auth/me` said on
 * boot, and what the API says afterwards. A tab left open outlives the answer it
 * started with, so a later `403 email_not_verified` — from any route — is taken
 * as the newer truth and flips the flag back to pending.
 */

interface SessionResponse {
  user: AuthenticatedUser;
}

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  /** True until the initial `GET /api/auth/me` settles. */
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Spends the token that came in the confirmation link. */
  verifyEmail: (token: string) => Promise<void>;
  /** Asks the API for another confirmation link. */
  resendVerification: () => Promise<void>;
  /** Changes the nickname and the display name of the signed-in account. */
  updateProfile: (input: UpdateProfileInput) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  // A 401 from any request means the session is gone, wherever it happened.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  /**
   * A `403 email_not_verified` from any request means the address is still
   * pending, wherever it happened. The session stays — it is valid, and the
   * confirmation screen needs it to show the address and to ask for another
   * link — only the flag moves, and the router does the rest.
   */
  useEffect(() => {
    setEmailNotVerifiedHandler(() => {
      setUser((current) =>
        current === null || !current.emailVerified ? current : { ...current, emailVerified: false },
      );
    });
    return () => setEmailNotVerifiedHandler(null);
  }, []);

  /** Re-reads the session from the server, tolerating not having one. */
  const refresh = useCallback(async () => {
    try {
      const session = await apiFetch<SessionResponse>('/api/auth/me');
      setUser(session.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let active = true;

    apiFetch<SessionResponse>('/api/auth/me')
      .then((session) => {
        if (active) {
          setUser(session.user);
        }
      })
      // No session, or the server is unreachable: either way the visitor is
      // anonymous and the login screen is the correct answer.
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const session = await apiFetch<SessionResponse>('/api/auth/login', {
      method: 'POST',
      body: input,
    });
    setUser(session.user);
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const session = await apiFetch<SessionResponse>('/api/auth/register', {
      method: 'POST',
      body: input,
    });
    setUser(session.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch<void>('/api/auth/logout', { method: 'POST' });
    } finally {
      // The local session ends even if the server could not be told.
      setUser(null);
    }
  }, []);

  /**
   * The confirmation route answers with the account the token belonged to, and
   * that account is *not* necessarily the visitor: the link is often opened in
   * a mail client on another device, with no session at all. So the answer is
   * dropped and the session is re-read instead — whoever is actually signed in
   * here gets their state refreshed, and nobody else is signed in by accident.
   */
  const verifyEmail = useCallback(
    async (token: string) => {
      await apiFetch<SessionResponse>('/api/auth/verify-email', {
        method: 'POST',
        body: { token },
      });
      await refresh();
    },
    [refresh],
  );

  const resendVerification = useCallback(async () => {
    await apiFetch<void>('/api/auth/resend-verification', { method: 'POST' });
  }, []);

  const updateProfile = useCallback(async (input: UpdateProfileInput) => {
    const session = await apiFetch<SessionResponse>('/api/users/me', {
      method: 'PATCH',
      body: input,
    });
    setUser(session.user);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      verifyEmail,
      resendVerification,
      updateProfile,
    }),
    [user, loading, login, register, logout, verifyEmail, resendVerification, updateProfile],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth precisa ser chamado dentro de <AuthProvider>.');
  }
  return value;
}
